import { motion } from 'framer-motion'
import { AlertTriangle, Leaf, Recycle, ClipboardCheck, Shield, Users, CheckCircle } from 'lucide-react'
import { hse } from '@data/company'
import AnimatedSection from '@components/AnimatedSection'

const iconMap: Record<string, React.ElementType> = {
  AlertTriangle, Leaf, Recycle, ClipboardCheck, Shield, Users
}

export default function HSEPage() {
  return (
    <>
      {/* Page Header */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-martimma-yellow/10 to-transparent" />
        <div className="section-padding max-w-7xl mx-auto relative z-10">
          <AnimatedSection>
            <span className="text-martimma-yellow text-sm font-semibold tracking-wider uppercase">HSE & Quality</span>
            <h1 className="heading-xl mt-4 mb-6">{hse.title}</h1>
            <p className="body-lg max-w-3xl">{hse.description}</p>
          </AnimatedSection>
        </div>
      </section>

      {/* HSE Pillars */}
      <section className="py-20 relative">
        <div className="section-padding max-w-7xl mx-auto">
          <AnimatedSection className="text-center mb-16">
            <span className="text-martimma-red text-sm font-semibold tracking-wider uppercase">Our Framework</span>
            <h2 className="heading-lg mt-4">Six Pillars of Safety Excellence</h2>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {hse.pillars.map((pillar, index) => {
              const Icon = iconMap[pillar.icon] || Shield
              return (
                <AnimatedSection key={pillar.title} delay={index * 0.1}>
                  <motion.div
                    whileHover={{ y: -5 }}
                    className="glass-card p-8 h-full group"
                  >
                    <div className="w-14 h-14 rounded-2xl bg-martimma-red/10 flex items-center justify-center mb-6 group-hover:bg-martimma-red/20 transition-colors">
                      <Icon className="w-7 h-7 text-martimma-red" />
                    </div>
                    <h3 className="text-white font-semibold text-lg mb-3">{pillar.title}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{pillar.description}</p>
                  </motion.div>
                </AnimatedSection>
              )
            })}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-24 bg-martimma-dark-light">
        <div className="section-padding max-w-7xl mx-auto">
          <AnimatedSection className="text-center mb-16">
            <span className="text-martimma-yellow text-sm font-semibold tracking-wider uppercase">Certifications</span>
            <h2 className="heading-lg mt-4">Certified, Audited, Trusted</h2>
            <p className="body-lg max-w-2xl mx-auto mt-4">
              We meet and exceed industry standards through rigorous quality management and regulatory compliance.
            </p>
          </AnimatedSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {hse.certifications.map((cert, index) => (
              <AnimatedSection key={cert.name} delay={index * 0.1}>
                <div className="glass-card p-8 text-center group hover:border-martimma-yellow/20 transition-colors">
                  <div className="w-16 h-16 rounded-full bg-martimma-yellow/10 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-martimma-yellow" />
                  </div>
                  <h3 className="text-white font-bold text-lg mb-2">{cert.name}</h3>
                  <p className="text-gray-400 text-sm mb-3">{cert.description}</p>
                  <span className="inline-block px-3 py-1 rounded-full bg-martimma-yellow/10 text-martimma-yellow text-xs font-medium">
                    {cert.status}
                  </span>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Local Content */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-martimma-red/10 to-martimma-yellow/10" />
        <div className="section-padding max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <AnimatedSection>
              <span className="text-martimma-red text-sm font-semibold tracking-wider uppercase">Local Content</span>
              <h2 className="heading-lg mt-4 mb-6">Proudly Powering Nigeria, by Nigerians</h2>
              <div className="text-8xl font-bold text-gradient mb-6">95%</div>
              <p className="text-gray-300 leading-relaxed mb-8">
                Minimum Nigerian Content Target. We are aligned with the Nigerian Oil and Gas Industry Content Development Act, investing in indigenous talent and continuous skills training.
              </p>
              <div className="space-y-4">
                {[
                  "Local Workforce Development — Investing in indigenous talent and continuous skills training",
                  "Local Procurement First — Prioritizing Nigerian suppliers, vendors, and service providers",
                  "Capacity Building — Knowledge transfer, mentorship, and technical training",
                  "Community Investment — Hiring locally, supporting host communities"
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-martimma-yellow shrink-0 mt-0.5" />
                    <span className="text-gray-300 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.2}>
              <div className="glass-card p-10 aspect-square flex flex-col items-center justify-center">
                <div className="text-center">
                  <Users className="w-20 h-20 text-martimma-yellow/30 mx-auto mb-6" />
                  <div className="text-6xl font-bold text-martimma-yellow mb-2">500+</div>
                  <p className="text-gray-400 mb-8">Direct Jobs Created</p>
                  <div className="grid grid-cols-2 gap-6 text-center">
                    <div>
                      <div className="text-3xl font-bold text-white">50+</div>
                      <p className="text-gray-500 text-sm">Indigenous Suppliers</p>
                    </div>
                    <div>
                      <div className="text-3xl font-bold text-white">6</div>
                      <p className="text-gray-500 text-sm">States Impacted</p>
                    </div>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </>
  )
}
